import { colors } from '@kmns-internal/furikake'
import {
  ContentCopyOutlined,
  DeleteOutlined,
  MoreHoriz,
} from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Menu,
  MenuItem,
} from '@mui/material'
import type {
  GetSchedulesRequest,
  Schedule,
} from '@ulysses-inc/harami_api_client'
import { type FC, type MouseEvent, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { findScheduleEveryOverLimit } from 'src/features/schedules/scheduleFrequencyLimits'
import schedulesOperations from 'src/state/ducks/schedules/operations'
import type { GetSchedulesFilter } from 'src/state/ducks/schedules/types'
import type { RootState } from 'src/state/store'
import type { TBaseColumnProps } from './ScheduleListRow'

const ActionButtons: FC<TBaseColumnProps> = ({ schedule }) => {
  const request = useSelector(
    (state: RootState) => state.schedulesState.schedules.request,
  )
  const filter = useSelector(
    (state: RootState) => state.schedulesState.schedules.filter,
  )
  const { showErrorSnackbar } = useSnackbar()

  // 3点リーダーメニュー
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }
  const handleMenuClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(null)
  }
  // 複製 MenuItem
  const handleMenuDuplicate = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    if (schedule !== undefined) {
      // 複製は編集フォームの検証を通らないので、周期の上限だけここで確かめる（上限超えの既存スケジュールを増やさない）
      const overLimit = findScheduleEveryOverLimit(schedule)
      if (overLimit) {
        showErrorSnackbar(
          `繰り返しの周期が上限（${overLimit.max}${overLimit.unit}ごと）を超えているため複製できません。周期を上限以下に変更してから複製してください`,
        )
        handleMenuClose(event)
        return
      }
      // API送信用のScheduleオブジェクトを作成
      const scheduleToCopy: Schedule = {
        id: 0,
        name: `${schedule.name}_コピー`,
        templates: schedule.templates,
        placeNodes:
          schedule.placeNodes?.map(node => ({ uuid: node.uuid })) ?? [],
        type: schedule.type,
        frequency: schedule.frequency,
        frequencyWeekly: schedule.frequencyWeekly
          ? {
              every: schedule.frequencyWeekly.every,
              dayOfWeeks: schedule.frequencyWeekly.dayOfWeeks,
            }
          : undefined,
        frequencyMonthly: schedule.frequencyMonthly
          ? {
              every: schedule.frequencyMonthly.every,
            }
          : undefined,
        frequencyCustom: schedule.frequencyCustom
          ? {
              every: schedule.frequencyCustom.every,
            }
          : undefined,
        startReportDate: schedule.startReportDate,
        endReportDate: schedule.endReportDate,
        startTime: schedule.startTime,
        endTime: schedule.endTime,
        isActive: schedule.isActive,
        userGroups: schedule.userGroups ?? [],
      }
      schedulesOperations.copySchedule(
        dispatch,
        scheduleToCopy,
        request,
        filter,
      )
    }
    handleMenuClose(event)
  }
  // 削除 MenuItem
  const handleMenuDelete = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    if (schedule !== undefined) {
      setDeleteDialogOpen(true)
    }
    handleMenuClose(event)
  }
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const handleDeleteConfirm = () => {
    if (schedule?.uuid) {
      deleteSchedule(schedule.uuid, request, filter)
    }
    setDeleteDialogOpen(false)
  }
  const dispatch = useDispatch()
  const deleteSchedule = (
    scheduleId: string,
    request: GetSchedulesRequest,
    filter?: GetSchedulesFilter,
  ) => {
    schedulesOperations.deleteSchedule(dispatch, scheduleId, request, filter)
  }

  return (
    <>
      <IconButton
        onClick={e => {
          e.stopPropagation()
          e.preventDefault()
          handleMenuClick(e)
        }}
      >
        <MoreHoriz />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClick={e => {
          e.stopPropagation()
        }}
      >
        <MenuItem
          onClick={e => handleMenuDuplicate(e)}
          disabled={!schedule}
          sx={{ gap: 1 }}
        >
          <ContentCopyOutlined />
          複製
        </MenuItem>
        <MenuItem
          onClick={e => handleMenuDelete(e)}
          disabled={!schedule}
          sx={{
            color: colors.error.main,
            '&:hover': {
              backgroundColor: colors.error.hover,
              color: colors.error.main,
            },
            gap: 1,
          }}
        >
          <DeleteOutlined />
          削除
        </MenuItem>
      </Menu>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false)
        }}
        onClick={e => {
          e.stopPropagation()
        }}
        slotProps={{
          backdrop: {
            onClick: e => {
              e.stopPropagation()
            },
          },
        }}
      >
        <DialogTitle>
          {schedule?.excelConversionFlows?.length
            ? '以下のExcel変換設定に紐づいています。削除を実行しますがよろしいですか?'
            : `「${schedule?.name}」の削除を実行しますがよろしいですか?`}
        </DialogTitle>
        {schedule?.excelConversionFlows?.length ? (
          <DialogContent>
            <List>
              {schedule?.excelConversionFlows.map(flow => (
                <ListItem key={flow.flowUUID}>{flow.flowName}</ListItem>
              ))}
            </List>
          </DialogContent>
        ) : null}
        <DialogActions>
          <Button
            onClick={e => {
              e.stopPropagation()
              setDeleteDialogOpen(false)
            }}
            variant="outlined"
          >
            キャンセル
          </Button>
          <Button
            onClick={e => {
              e.stopPropagation()
              handleDeleteConfirm()
            }}
            color="error"
            variant="contained"
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default ActionButtons
